import {
  BOT_GET_TOKEN_INFO_QUERY_KEY,
  Pair,
  useGetBotTokenInfo,
} from "@/api/bot/bot-get-token-info";
import { useBotSwitchChainMutation } from "@/api/bot/bot-switch-chain";
import { getUserId } from "@/helpers/localStorage";
import { useGetScreenerToken } from "@/api/token-screener/screener-tokens";
import { ScreenerTokenRaw } from "@/api/token-screener/types";
import { getToken } from "@/helpers/localStorage";
import { BotWalletSupportedChain, useWallet } from "@/providers/wallet";
import { createContext, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import {
  BOT_GET_USER_INFO_QUERY_KEY,
  useGetBotUserInfo,
} from "@/api/bot/bot-get-user-info";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { useQueryClient } from "@tanstack/react-query";

export type TokenContextType =
  | ({
      balanceNative: string;
      balance: string;
      balanceUsd: number;
      priceUsd: number;
      boughtBalance: number;
      boughtBalanceUsd: number;
      soldBalance: number;
      soldBalanceUSD: number;
      netPNLUsd: number;
      netPNLPercent: number;
      botPair: Pair;
      quoteToken: "token0" | "token1";
      // ...all fields from ScreenerTokenRaw
    } & ScreenerTokenRaw & {
        isLoading?: boolean;
      })
  | null;

export const TokenContext = createContext<TokenContextType>(null);

const TokenProvider = ({
  tokenAddressAndChain,
  children,
}: {
  tokenAddressAndChain?: string | null;
  children: React.ReactNode;
}) => {
  const { tokenId: tokenIdParams } = useParams();
  const tokenId = tokenAddressAndChain || tokenIdParams;

  const tokenAddress = tokenId?.split(":")[0];
  const { currentWallet: wallet } = useWallet();
  const { data, isLoading } = useGetScreenerToken({
    tokenId: tokenId,
  });

  const { isConnected } = useWeb3Auth();
  const queryClient = useQueryClient();

  const { appPubKey, userId } = useWallet();

  const { mutate: mutateSwitchChain } = useBotSwitchChainMutation();

  const botChainId = tokenId?.startsWith("0x")
    ? BotWalletSupportedChain.ETH
    : BotWalletSupportedChain.SOL; // TODO: This should be handled dynamically. The backend API should provide a chain for each token, and there should be a mapping between the backend API chain and the bot API chain

  useEffect(() => {
    if (!tokenId) {
      return;
    }

    mutateSwitchChain({
      appPubKey,
      userId,
      chainId: botChainId,
      idToken: getToken() || "",
    });
  }, [tokenId]);

  const { data: botTokenInfo } = useGetBotTokenInfo({
    appPubKey: appPubKey,
    chainId: botChainId,
    tokenAddress: tokenAddress || "",
    userId: getUserId() || "",
  });

  useEffect(() => {
    if (!isConnected) {
      return;
    }
    queryClient.invalidateQueries({
      queryKey: [
        BOT_GET_TOKEN_INFO_QUERY_KEY,
        {
          chainId: botChainId,
          tokenAddress: tokenAddress || "",
          userId: getUserId() || "",
        },
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [BOT_GET_USER_INFO_QUERY_KEY, { userId: getUserId() || "" }],
    });
  }, [isConnected, queryClient, botChainId, tokenAddress]);

  const { data: userInfo } = useGetBotUserInfo({
    appPubKey: appPubKey,
    userId: getUserId() || "",
  });

  const token = useMemo(
    () =>
      data?.results?.find((token) => token.address === tokenAddress) || null,
    [data, tokenAddress],
  );

  const tokenData = useMemo(
    () =>
      ({
        ...token,
        botPair: botTokenInfo?.pair,
        balanceNative: userInfo?.balances?.[wallet?.botWalletIndex ?? 0] || 0,
        balance:
          botTokenInfo?.tokenUserBalances?.[wallet?.botWalletIndex ?? 0] || 0,
        balanceUsd:
          (botTokenInfo?.pair?.priceUsd || 0) *
          (Number(
            botTokenInfo?.tokenUserBalances?.[wallet?.botWalletIndex ?? 0],
          ) || 0),
        priceUsd: botTokenInfo?.pair?.priceUsd || 0,
        // TODO: SHOULD ADD WHEN ADDED TO THE BOT
        boughtBalance: 0,
        boughtBalanceUsd: 0,
        soldBalance: 0,
        soldBalanceUSD: 0,
        netPNLUsd: 0,
        netPNLPercent: 0,
        isLoading,
      }) as TokenContextType,
    [
      token,
      botTokenInfo?.pair,
      botTokenInfo?.tokenUserBalances,
      userInfo?.balances,
      wallet?.botWalletIndex,
      isLoading,
    ],
  );

  return (
    <TokenContext.Provider value={tokenData}>{children}</TokenContext.Provider>
  );
};

export default TokenProvider;
